import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { companiesAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Slider } from "../components/ui/slider";
import { Separator } from "../components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Save,
  Loader2,
  Upload,
  Download,
  Image as ImageIcon,
  Type,
  Square,
  Move,
  Eye,
  ArrowLeft,
  Trash2,
  ExternalLink,
  FileJson
} from "lucide-react";
import { Link } from "react-router-dom";

const DEFAULT_CONFIG = {
  enabled: true,
  background_image: null,
  headline_enabled: true,
  headline_text: "Photovoltaik All Inclusive Angebot",
  headline_position_x: 55,
  headline_position_y: 8,
  headline_font_size: 24,
  headline_font_color: "#000000",
  logo_enabled: true,
  logo_position_x: 52,
  logo_position_y: 5,
  logo_width: 120,
  info_box_enabled: true,
  info_box_position_x: 52,
  info_box_position_y: 55,
  info_box_width: 220,  // Width in points (~7.8cm) - wider for 2 columns
  info_box_height: 130, // Height in points (~4.6cm) - shorter for 2 columns
  info_box_border_color: "#000000",
  info_box_background_color: "#FFFFFF",
  info_box_font_size: 9,
  info_box_font_color: "#333333",
  info_box_label_color: "#666666",
  show_offer_number: true,
  show_date: true,
  show_customer_name: true,
  show_customer_address: true,
  show_system_size: true,
  show_storage_size: true,
  show_created_by: true,
};

// Sample data for preview
const SAMPLE_OFFER = {
  offer_number: "A-2026-0001",
  date: "08.02.2026",
  customer_name: "Max Mustermann",
  customer_address: "Musterstraße 1, 12345 Berlin",
  system_size: "10.67 kWp",
  module_count: "22 Module",
  storage_size: "15 kWh",
  created_by: "Super Admin"
};

const DEFAULT_FINANCING_CONFIG = {
  enabled: false,
  background_image: null,
  rate_10y_interest: "4,39 %",
  rate_10y_eff: "4,9 % eff.",
  rate_10y_monthly: "245,42 €",
  rate_15y_interest: "4,69 %",
  rate_15y_eff: "4,9 % eff.",
  rate_15y_monthly: "188,56 €",
  rate_20y_interest: "4,89 %",
  rate_20y_eff: "4,9 % eff.",
  rate_20y_monthly: "136,16 €",
};

export default function CoverPageEditorPage() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(user?.active_company_id || "");
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [financingConfig, setFinancingConfig] = useState(DEFAULT_FINANCING_CONFIG);
  const [logoUrl, setLogoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingFinancing, setSavingFinancing] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingFinancingImage, setUploadingFinancingImage] = useState(false);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const fileInputRef = useRef(null);
  const financingFileInputRef = useRef(null);
  const importFileInputRef = useRef(null);
  
  // Import state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importData, setImportData] = useState(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompanyId) {
      loadConfig(selectedCompanyId);
      loadFinancingConfig(selectedCompanyId);
    }
  }, [selectedCompanyId]);

  const loadCompanies = async () => {
    try {
      const response = await companiesAPI.list();
      setCompanies(response.data);
      if (response.data.length > 0 && !selectedCompanyId) {
        setSelectedCompanyId(response.data[0].id);
      }
    } catch (error) {
      console.error("Failed to load companies:", error);
      toast.error("Fehler beim Laden der Firmen");
    }
  };

  const loadConfig = async (companyId) => {
    setLoading(true);
    try {
      const response = await companiesAPI.getCoverPageConfig(companyId);
      const loadedConfig = response.data.config || DEFAULT_CONFIG;
      setConfig({ ...DEFAULT_CONFIG, ...loadedConfig });
      setLogoUrl(response.data.logo_url);
    } catch (error) {
      console.error("Failed to load config:", error);
      setConfig(DEFAULT_CONFIG);
    } finally {
      setLoading(false);
    }
  };

  const loadFinancingConfig = async (companyId) => {
    try {
      const response = await companiesAPI.getFinancingPageConfig(companyId);
      const loadedConfig = response.data.config || DEFAULT_FINANCING_CONFIG;
      setFinancingConfig({ ...DEFAULT_FINANCING_CONFIG, ...loadedConfig });
    } catch (error) {
      console.error("Failed to load financing config:", error);
      setFinancingConfig(DEFAULT_FINANCING_CONFIG);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await companiesAPI.updateCoverPageConfig(selectedCompanyId, config);
      toast.success("Deckblatt-Konfiguration gespeichert");
    } catch (error) {
      console.error("Failed to save config:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFinancing = async () => {
    setSavingFinancing(true);
    try {
      await companiesAPI.updateFinancingPageConfig(selectedCompanyId, financingConfig);
      toast.success("Finanzierungsseite gespeichert");
    } catch (error) {
      console.error("Failed to save financing config:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSavingFinancing(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Nur PNG, JPEG oder WebP Dateien erlaubt");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Bild darf maximal 5MB groß sein");
      return;
    }

    setUploadingImage(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = (e) => {
        setConfig(prev => ({ ...prev, background_image: e.target.result }));
        setUploadingImage(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error("Fehler beim Hochladen");
      setUploadingImage(false);
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDeleteImage = () => {
    setConfig(prev => ({ ...prev, background_image: null }));
  };

  const handleFinancingImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Nur PNG, JPEG oder WebP Dateien erlaubt");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Bild darf maximal 5MB groß sein");
      return;
    }

    setUploadingFinancingImage(true);
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFinancingConfig(prev => ({ ...prev, background_image: e.target.result }));
        setUploadingFinancingImage(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error("Fehler beim Hochladen");
      setUploadingFinancingImage(false);
    }
    
    if (financingFileInputRef.current) {
      financingFileInputRef.current.value = "";
    }
  };

  const handleDeleteFinancingImage = () => {
    setFinancingConfig(prev => ({ ...prev, background_image: null }));
  };

  const updateFinancingConfig = (key, value) => {
    setFinancingConfig(prev => ({ ...prev, [key]: value }));
  };

  // Export cover page config
  const handleExportConfig = async () => {
    try {
      const response = await companiesAPI.exportCoverPageConfig(selectedCompanyId);
      
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const company = companies.find(c => c.id === selectedCompanyId);
      const safeName = (company?.name || 'Firma').replace(/[^a-zA-Z0-9]/g, '_');
      a.download = `${safeName}_Deckblatt_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success("Deckblatt-Konfiguration exportiert");
    } catch (error) {
      console.error("Failed to export config:", error);
      toast.error("Fehler beim Exportieren der Deckblatt-Konfiguration");
    }
  };

  // Handle import file selection
  const handleImportFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      const content = await file.text();
      const data = JSON.parse(content);
      
      // Validate
      if (data.export_type !== 'cover_page_config') {
        toast.error("Ungültiges Backup-Format. Bitte wählen Sie eine Deckblatt-Konfigurationsdatei.");
        return;
      }
      
      setImportData(data);
      setImportDialogOpen(true);
    } catch (error) {
      console.error("Failed to read file:", error);
      toast.error("Fehler beim Lesen der Datei");
    }
    
    // Reset input
    event.target.value = '';
  };

  // Confirm import
  const handleImportConfirm = async () => {
    if (!importData) return;
    
    setImporting(true);
    try {
      // Create a file blob from the import data
      const blob = new Blob([JSON.stringify(importData)], { type: 'application/json' });
      const file = new File([blob], 'import.json', { type: 'application/json' });
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await companiesAPI.importCoverPageConfig(selectedCompanyId, formData);
      
      if (response.data.success) {
        toast.success(response.data.message);
        // Reload the config
        await loadConfig(selectedCompanyId);
      } else {
        toast.error("Import fehlgeschlagen");
      }
    } catch (error) {
      console.error("Failed to import config:", error);
      const errorMsg = error.response?.data?.detail || "Fehler beim Importieren";
      toast.error(errorMsg);
    } finally {
      setImporting(false);
      setImportDialogOpen(false);
      setImportData(null);
    }
  };

  const handleOpenPreview = async () => {
    if (!config.background_image) {
      toast.error("Bitte laden Sie zuerst ein Hintergrundbild hoch");
      return;
    }

    setGeneratingPreview(true);
    try {
      // First save the current config
      await companiesAPI.updateCoverPageConfig(selectedCompanyId, config);
      
      // Get the preview URL
      const token = localStorage.getItem("token");
      const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
      const previewUrl = `${baseUrl}/api/companies/${selectedCompanyId}/cover-page-preview?token=${token}`;
      
      // Open in new window
      window.open(previewUrl, "_blank", "width=800,height=1100");
    } catch (error) {
      console.error("Failed to generate preview:", error);
      toast.error("Fehler beim Generieren der Vorschau");
    } finally {
      setGeneratingPreview(false);
    }
  };

  const updateConfig = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hidden file input for import */}
      <input
        type="file"
        ref={importFileInputRef}
        accept=".json"
        onChange={handleImportFileSelect}
        style={{ display: 'none' }}
      />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/firmen-ci">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold">Deckblatt-Editor</h1>
            <p className="text-muted-foreground">Gestalten Sie das Deckblatt für Ihre Angebots-PDFs</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleExportConfig}
            title="Deckblatt-Konfiguration exportieren"
            data-testid="export-cover-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportieren
          </Button>
          <Button 
            variant="outline" 
            onClick={() => importFileInputRef.current?.click()}
            title="Deckblatt-Konfiguration importieren"
            data-testid="import-cover-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            Importieren
          </Button>
          <Button 
            variant="outline" 
            onClick={handleOpenPreview} 
            disabled={generatingPreview || !config.background_image}
            data-testid="preview-cover-btn"
          >
            {generatingPreview ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <ExternalLink className="h-4 w-4 mr-2" />
            )}
            Vorschau
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-cover-config-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Speichern
          </Button>
        </div>
      </div>

      {/* Company Selector */}
      {companies.length > 1 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Label>Firma auswählen:</Label>
              <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                <SelectTrigger className="w-[300px]">
                  <SelectValue placeholder="Firma wählen" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column: Settings */}
        <div className="space-y-6">
          {/* Enable/Disable */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Deckblatt aktivieren
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <Label>Deckblatt in PDFs anzeigen</Label>
                <Switch
                  checked={config.enabled}
                  onCheckedChange={(checked) => updateConfig("enabled", checked)}
                  data-testid="cover-enabled-switch"
                />
              </div>
            </CardContent>
          </Card>

          {/* Background Image */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="h-5 w-5" />
                Hintergrundbild
              </CardTitle>
              <CardDescription>Empfohlen: 1920x1080px oder A4-Format, max. 5MB</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {config.background_image ? (
                <div className="space-y-4">
                  <img
                    src={config.background_image}
                    alt="Hintergrund"
                    className="max-h-32 object-contain rounded border"
                  />
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploadingImage}>
                      {uploadingImage ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                      Ändern
                    </Button>
                    <Button variant="outline" onClick={handleDeleteImage} className="text-destructive">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Löschen
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="border-2 border-dashed rounded-lg p-6 text-center">
                  <ImageIcon className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground mb-2">Kein Hintergrundbild</p>
                  <Button onClick={() => fileInputRef.current?.click()} disabled={uploadingImage}>
                    {uploadingImage ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                    Bild hochladen
                  </Button>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                onChange={handleImageUpload}
                className="hidden"
              />
            </CardContent>
          </Card>

          {/* Headline */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Type className="h-5 w-5" />
                Überschrift
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Überschrift anzeigen</Label>
                <Switch
                  checked={config.headline_enabled}
                  onCheckedChange={(checked) => updateConfig("headline_enabled", checked)}
                />
              </div>
              
              {config.headline_enabled && (
                <>
                  <div className="space-y-2">
                    <Label>Text</Label>
                    <Input
                      value={config.headline_text}
                      onChange={(e) => updateConfig("headline_text", e.target.value)}
                      placeholder="Überschrift eingeben"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Position X (%)</Label>
                      <div className="flex items-center gap-2">
                        <Slider
                          value={[config.headline_position_x]}
                          onValueChange={([val]) => updateConfig("headline_position_x", val)}
                          max={100}
                          step={1}
                        />
                        <span className="w-12 text-sm">{config.headline_position_x}%</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Position Y (%)</Label>
                      <div className="flex items-center gap-2">
                        <Slider
                          value={[config.headline_position_y]}
                          onValueChange={([val]) => updateConfig("headline_position_y", val)}
                          max={100}
                          step={1}
                        />
                        <span className="w-12 text-sm">{config.headline_position_y}%</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Schriftgröße</Label>
                      <Input
                        type="number"
                        value={config.headline_font_size}
                        onChange={(e) => updateConfig("headline_font_size", parseInt(e.target.value) || 24)}
                        min={10}
                        max={48}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Schriftfarbe</Label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={config.headline_font_color}
                          onChange={(e) => updateConfig("headline_font_color", e.target.value)}
                          className="h-10 w-14 rounded cursor-pointer"
                        />
                        <Input
                          value={config.headline_font_color}
                          onChange={(e) => updateConfig("headline_font_color", e.target.value)}
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Logo */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="h-5 w-5" />
                Firmenlogo
              </CardTitle>
              <CardDescription>Das Logo aus den Firmeneinstellungen</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Logo anzeigen</Label>
                <Switch
                  checked={config.logo_enabled}
                  onCheckedChange={(checked) => updateConfig("logo_enabled", checked)}
                />
              </div>
              
              {config.logo_enabled && (
                <>
                  {logoUrl && (
                    <div className="border rounded p-2 bg-gray-50 dark:bg-gray-900">
                      <img src={logoUrl} alt="Logo" className="max-h-12 object-contain" />
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Position X (%)</Label>
                      <div className="flex items-center gap-2">
                        <Slider
                          value={[config.logo_position_x]}
                          onValueChange={([val]) => updateConfig("logo_position_x", val)}
                          max={100}
                          step={1}
                        />
                        <span className="w-12 text-sm">{config.logo_position_x}%</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Position Y (%)</Label>
                      <div className="flex items-center gap-2">
                        <Slider
                          value={[config.logo_position_y]}
                          onValueChange={([val]) => updateConfig("logo_position_y", val)}
                          max={100}
                          step={1}
                        />
                        <span className="w-12 text-sm">{config.logo_position_y}%</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Logo-Breite (Punkte)</Label>
                    <Input
                      type="number"
                      value={config.logo_width}
                      onChange={(e) => updateConfig("logo_width", parseInt(e.target.value) || 120)}
                      min={50}
                      max={300}
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Info Box */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Square className="h-5 w-5" />
                Info-Box
              </CardTitle>
              <CardDescription>Angebotsdaten-Kasten auf dem Deckblatt</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Info-Box anzeigen</Label>
                <Switch
                  checked={config.info_box_enabled}
                  onCheckedChange={(checked) => updateConfig("info_box_enabled", checked)}
                />
              </div>
              
              {config.info_box_enabled && (
                <>
                  <Separator />
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Position X (%)</Label>
                      <div className="flex items-center gap-2">
                        <Slider
                          value={[config.info_box_position_x]}
                          onValueChange={([val]) => updateConfig("info_box_position_x", val)}
                          max={100}
                          step={1}
                        />
                        <span className="w-12 text-sm">{config.info_box_position_x}%</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Position Y (%)</Label>
                      <div className="flex items-center gap-2">
                        <Slider
                          value={[config.info_box_position_y]}
                          onValueChange={([val]) => updateConfig("info_box_position_y", val)}
                          max={100}
                          step={1}
                        />
                        <span className="w-12 text-sm">{config.info_box_position_y}%</span>
                      </div>
                    </div>
                  </div>

                  <Separator />
                  <h4 className="font-medium">Größe</h4>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Breite (Punkte)</Label>
                      <div className="flex items-center gap-2">
                        <Slider
                          value={[config.info_box_width || 150]}
                          onValueChange={([val]) => updateConfig("info_box_width", val)}
                          min={80}
                          max={250}
                          step={5}
                        />
                        <span className="w-12 text-sm">{config.info_box_width || 150}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">~{((config.info_box_width || 150) / 28.35).toFixed(1)} cm</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Höhe (Punkte)</Label>
                      <div className="flex items-center gap-2">
                        <Slider
                          value={[config.info_box_height || 210]}
                          onValueChange={([val]) => updateConfig("info_box_height", val)}
                          min={100}
                          max={350}
                          step={5}
                        />
                        <span className="w-12 text-sm">{config.info_box_height || 210}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">~{((config.info_box_height || 210) / 28.35).toFixed(1)} cm</p>
                    </div>
                  </div>

                  <Separator />
                  <h4 className="font-medium">Design</h4>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Rahmenfarbe</Label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={config.info_box_border_color}
                          onChange={(e) => updateConfig("info_box_border_color", e.target.value)}
                          className="h-10 w-14 rounded cursor-pointer"
                        />
                        <Input
                          value={config.info_box_border_color}
                          onChange={(e) => updateConfig("info_box_border_color", e.target.value)}
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Hintergrundfarbe</Label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={config.info_box_background_color}
                          onChange={(e) => updateConfig("info_box_background_color", e.target.value)}
                          className="h-10 w-14 rounded cursor-pointer"
                        />
                        <Input
                          value={config.info_box_background_color}
                          onChange={(e) => updateConfig("info_box_background_color", e.target.value)}
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Schriftgröße</Label>
                      <Input
                        type="number"
                        value={config.info_box_font_size}
                        onChange={(e) => updateConfig("info_box_font_size", parseInt(e.target.value) || 9)}
                        min={6}
                        max={14}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Textfarbe</Label>
                      <div className="flex gap-1">
                        <input
                          type="color"
                          value={config.info_box_font_color}
                          onChange={(e) => updateConfig("info_box_font_color", e.target.value)}
                          className="h-10 w-10 rounded cursor-pointer"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Label-Farbe</Label>
                      <div className="flex gap-1">
                        <input
                          type="color"
                          value={config.info_box_label_color}
                          onChange={(e) => updateConfig("info_box_label_color", e.target.value)}
                          className="h-10 w-10 rounded cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />
                  <h4 className="font-medium">Angezeigte Felder</h4>

                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { key: "show_offer_number", label: "Angebotsnummer" },
                      { key: "show_date", label: "Datum" },
                      { key: "show_customer_name", label: "Kundenname" },
                      { key: "show_customer_address", label: "Kundenadresse" },
                      { key: "show_system_size", label: "Anlagengröße" },
                      { key: "show_storage_size", label: "Speichergröße" },
                      { key: "show_created_by", label: "Erstellt von" },
                    ].map(({ key, label }) => (
                      <div key={key} className="flex items-center justify-between">
                        <Label className="text-sm">{label}</Label>
                        <Switch
                          checked={config[key]}
                          onCheckedChange={(checked) => updateConfig(key, checked)}
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Preview */}
        <div className="lg:sticky lg:top-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Vorschau
              </CardTitle>
              <CardDescription>Live-Vorschau des Deckblatts</CardDescription>
            </CardHeader>
            <CardContent>
              <div
                className="relative bg-white border rounded-lg overflow-hidden"
                style={{ aspectRatio: "210/297" }} // A4 aspect ratio
              >
                {/* Background Image */}
                {config.background_image ? (
                  <img
                    src={config.background_image}
                    alt="Hintergrund"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                    <span className="text-gray-400">Kein Hintergrundbild</span>
                  </div>
                )}

                {config.enabled && (
                  <>
                    {/* Logo */}
                    {config.logo_enabled && logoUrl && (
                      <div
                        className="absolute"
                        style={{
                          left: `${config.logo_position_x}%`,
                          top: `${config.logo_position_y}%`,
                          transform: "translate(-50%, 0)"
                        }}
                      >
                        <img
                          src={logoUrl}
                          alt="Logo"
                          style={{ width: `${config.logo_width / 3}px` }}
                          className="object-contain"
                        />
                      </div>
                    )}

                    {/* Headline */}
                    {config.headline_enabled && (
                      <div
                        className="absolute text-center whitespace-nowrap"
                        style={{
                          left: `${config.headline_position_x}%`,
                          top: `${config.headline_position_y}%`,
                          transform: "translate(-50%, 0)",
                          fontSize: `${config.headline_font_size / 2}px`,
                          color: config.headline_font_color,
                          fontWeight: "bold"
                        }}
                      >
                        {config.headline_text}
                      </div>
                    )}

                    {/* Info Box */}
                    {config.info_box_enabled && (
                      <div
                        className="absolute p-2"
                        style={{
                          left: `${config.info_box_position_x}%`,
                          top: `${config.info_box_position_y}%`,
                          transform: "translate(-50%, 0)",
                          backgroundColor: config.info_box_background_color,
                          border: `1px solid ${config.info_box_border_color}`,
                          width: `${(config.info_box_width || 220) / 3}px`,
                          minHeight: `${(config.info_box_height || 130) / 3}px`,
                          fontSize: `${config.info_box_font_size / 1.5}px`,
                          overflow: "hidden"
                        }}
                      >
                        {/* Top Row: Customer name (full width) */}
                        {config.show_customer_name && (
                          <div className="mb-1 pb-1" style={{ borderBottom: `1px solid ${config.info_box_border_color}20` }}>
                            <div style={{ color: config.info_box_label_color, fontSize: "0.65em" }}>Kunde</div>
                            <div style={{ color: config.info_box_font_color, fontWeight: "bold", fontSize: "0.9em" }}>{SAMPLE_OFFER.customer_name}</div>
                            {config.show_customer_address && (
                              <div style={{ color: config.info_box_label_color, fontSize: "0.7em" }}>{SAMPLE_OFFER.customer_address}</div>
                            )}
                          </div>
                        )}
                        {/* Two columns below */}
                        <div style={{ display: "flex", gap: "4px" }}>
                          {/* Left Column */}
                          <div style={{ flex: 1 }}>
                            {config.show_offer_number && (
                              <div className="mb-1">
                                <div style={{ color: config.info_box_label_color, fontSize: "0.6em" }}>Angebotsnr.</div>
                                <div style={{ color: config.info_box_font_color, fontWeight: "bold", fontSize: "0.8em" }}>{SAMPLE_OFFER.offer_number}</div>
                                {config.show_date && (
                                  <div style={{ color: config.info_box_label_color, fontSize: "0.65em" }}>vom {SAMPLE_OFFER.date}</div>
                                )}
                              </div>
                            )}
                            {config.show_system_size && (
                              <div className="mb-1">
                                <div style={{ color: config.info_box_label_color, fontSize: "0.6em" }}>Anlage</div>
                                <div style={{ color: config.info_box_font_color, fontWeight: "bold", fontSize: "0.8em" }}>{SAMPLE_OFFER.system_size}</div>
                                <div style={{ color: config.info_box_label_color, fontSize: "0.65em" }}>{SAMPLE_OFFER.module_count}</div>
                              </div>
                            )}
                          </div>
                          {/* Right Column */}
                          <div style={{ flex: 1 }}>
                            {config.show_storage_size && (
                              <div className="mb-1">
                                <div style={{ color: config.info_box_label_color, fontSize: "0.6em" }}>Speicher</div>
                                <div style={{ color: config.info_box_font_color, fontWeight: "bold", fontSize: "0.8em" }}>{SAMPLE_OFFER.storage_size}</div>
                              </div>
                            )}
                            {config.show_created_by && (
                              <div>
                                <div style={{ color: config.info_box_label_color, fontSize: "0.6em" }}>Erstellt von</div>
                                <div style={{ color: config.info_box_font_color, fontWeight: "bold", fontSize: "0.8em" }}>{SAMPLE_OFFER.created_by}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {!config.enabled && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <span className="text-white font-medium">Deckblatt deaktiviert</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Die tatsächliche PDF-Darstellung kann leicht abweichen
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ========== FINANCING PAGE SECTION ========== */}
      <Separator className="my-8" />
      
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Finanzierungsseite</h2>
            <p className="text-muted-foreground">
              Diese Seite wird am Ende des Angebots vor den rechtlichen Texten eingefügt
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Label>Aktiviert</Label>
              <Switch
                checked={financingConfig.enabled}
                onCheckedChange={(checked) => updateFinancingConfig("enabled", checked)}
              />
            </div>
            <Button onClick={handleSaveFinancing} disabled={savingFinancing}>
              {savingFinancing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Speichern
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Settings */}
          <div className="space-y-6">
            {/* Background Image */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ImageIcon className="h-5 w-5" />
                  Hintergrundbild
                </CardTitle>
                <CardDescription>
                  Laden Sie Ihre Finanzierungsseite als Hintergrundbild hoch
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <input
                  type="file"
                  ref={financingFileInputRef}
                  onChange={handleFinancingImageUpload}
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  className="hidden"
                />
                
                {financingConfig.background_image ? (
                  <div className="space-y-2">
                    <div className="relative aspect-[210/297] w-full max-w-xs mx-auto border rounded overflow-hidden">
                      <img
                        src={financingConfig.background_image}
                        alt="Finanzierungsseite"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex gap-2 justify-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => financingFileInputRef.current?.click()}
                        disabled={uploadingFinancingImage}
                      >
                        {uploadingFinancingImage ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Upload className="h-4 w-4 mr-2" />
                        )}
                        Ersetzen
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleDeleteFinancingImage}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Entfernen
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => financingFileInputRef.current?.click()}
                    className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
                  >
                    {uploadingFinancingImage ? (
                      <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                    ) : (
                      <>
                        <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">
                          Klicken zum Hochladen
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          PNG, JPEG oder WebP, max. 5MB
                        </p>
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Financing Table Values */}
            <Card>
              <CardHeader>
                <CardTitle>Finanzierungstabelle</CardTitle>
                <CardDescription>
                  Bearbeiten Sie die Zinssätze und monatlichen Raten
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* 10 Jahre */}
                <div className="space-y-3">
                  <h4 className="font-medium text-sm">10 Jahre Laufzeit</h4>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Zinssatz</Label>
                      <Input
                        value={financingConfig.rate_10y_interest}
                        onChange={(e) => updateFinancingConfig("rate_10y_interest", e.target.value)}
                        placeholder="4,39 %"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Eff. Zinssatz</Label>
                      <Input
                        value={financingConfig.rate_10y_eff}
                        onChange={(e) => updateFinancingConfig("rate_10y_eff", e.target.value)}
                        placeholder="4,9 % eff."
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Mtl. Rate</Label>
                      <Input
                        value={financingConfig.rate_10y_monthly}
                        onChange={(e) => updateFinancingConfig("rate_10y_monthly", e.target.value)}
                        placeholder="245,42 €"
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* 15 Jahre */}
                <div className="space-y-3">
                  <h4 className="font-medium text-sm">15 Jahre Laufzeit</h4>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Zinssatz</Label>
                      <Input
                        value={financingConfig.rate_15y_interest}
                        onChange={(e) => updateFinancingConfig("rate_15y_interest", e.target.value)}
                        placeholder="4,69 %"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Eff. Zinssatz</Label>
                      <Input
                        value={financingConfig.rate_15y_eff}
                        onChange={(e) => updateFinancingConfig("rate_15y_eff", e.target.value)}
                        placeholder="4,9 % eff."
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Mtl. Rate</Label>
                      <Input
                        value={financingConfig.rate_15y_monthly}
                        onChange={(e) => updateFinancingConfig("rate_15y_monthly", e.target.value)}
                        placeholder="188,56 €"
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* 20 Jahre */}
                <div className="space-y-3">
                  <h4 className="font-medium text-sm">20 Jahre Laufzeit</h4>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Zinssatz</Label>
                      <Input
                        value={financingConfig.rate_20y_interest}
                        onChange={(e) => updateFinancingConfig("rate_20y_interest", e.target.value)}
                        placeholder="4,89 %"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Eff. Zinssatz</Label>
                      <Input
                        value={financingConfig.rate_20y_eff}
                        onChange={(e) => updateFinancingConfig("rate_20y_eff", e.target.value)}
                        placeholder="4,9 % eff."
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Mtl. Rate</Label>
                      <Input
                        value={financingConfig.rate_20y_monthly}
                        onChange={(e) => updateFinancingConfig("rate_20y_monthly", e.target.value)}
                        placeholder="136,16 €"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: Preview */}
          <div className="lg:sticky lg:top-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  Vorschau Finanzierungsseite
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className="relative bg-white border rounded-lg overflow-hidden"
                  style={{ aspectRatio: "210/297" }}
                >
                  {financingConfig.background_image ? (
                    <img
                      src={financingConfig.background_image}
                      alt="Finanzierungsseite Vorschau"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                      <span className="text-muted-foreground text-sm">
                        Kein Hintergrundbild hochgeladen
                      </span>
                    </div>
                  )}
                  
                  {!financingConfig.enabled && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="text-white font-medium">Finanzierungsseite deaktiviert</span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  Die Tabellenwerte werden automatisch über das Bild gelegt
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Import Confirmation Dialog */}
      <AlertDialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <FileJson className="h-5 w-5 text-primary" />
              Deckblatt-Konfiguration importieren
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800 text-sm">
                <p className="font-semibold">Achtung: Die aktuelle Konfiguration wird überschrieben!</p>
              </div>
              
              {importData && (
                <div className="bg-muted rounded-lg p-3 text-sm">
                  <p><strong>Quelle:</strong> {importData.source_company_name || 'Unbekannt'}</p>
                  <p><strong>Exportiert am:</strong> {importData.export_date ? new Date(importData.export_date).toLocaleString('de-DE') : 'Unbekannt'}</p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Hinweis: Das Hintergrundbild muss separat hochgeladen werden.
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={importing}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleImportConfirm}
              disabled={importing}
            >
              {importing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importiere...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Importieren
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
